// API client for checkout operations

// Widget mode: absolute URL for cross-origin requests
// Standalone mode: relative URL (same-origin worker)
let API_BASE = (window as any).__SAGAPAY_API_BASE__ || '/v1';

/** Set API base URL (called by widget.tsx before rendering) */
export function setApiBase(url: string) {
  API_BASE = url;
}

// =====================================================
// Types
// =====================================================

interface ApiResponse<T = Record<string, unknown>> {
  data?: T;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
    /** Set on METHOD_UNAVAILABLE: which payment method the store cannot take. */
    method?: string;
  };
}

export interface PostalCodeResponse {
  valid: boolean;
  city?: string;
}

export interface SavedAddressesResponse {
  addresses: SavedAddress[];
}

export interface SavedAddress {
  id: string;
  fullName?: string;
  phone?: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  countryCode?: string;
  isDefault?: boolean;
}

export interface FreeShippingProgress {
  enabled: boolean;
  currentValue: number;
  threshold: number;
  remaining: number;
  percentage: number;
  achieved: boolean;
  message: string;
}

export interface ShippingOptionsResponse {
  options: ShippingOption[];
  freeShippingApplied?: boolean;
  pricingStrategy?: string;
  freeShippingProgress?: FreeShippingProgress;
}

export interface ShippingOption {
  id: string;
  name?: string;
  displayName?: string;
  description: string;
  price: { amount: number; currency: string };
  estimatedDelivery: string | { date: string; display: string };
  provider: string;
  isPickup?: boolean;
  isCheapest?: boolean;
  isFastest?: boolean;
  // Pricing display fields
  originalPrice?: { amount: number; currency: string };
  discountAmount?: number;
  pricingLabel?: string;
  pricingMessage?: string;
  showOriginalPrice?: boolean;
  showSavings?: boolean;
  isFreeShipping?: boolean;
}

export interface PaymentMethodsResponse {
  paymentMethods: PaymentMethodInfo[];
  quickPayAvailable: boolean;
  lastUsedMethod?: string;
  // Surfboard SDK data for payment initialization
  surfboardOrderId?: string;
  surfboardNonce?: string;
  publicKey?: string;
}

export interface PaymentMethodInfo {
  id: string;
  name: string;
  icon: string;
  available: boolean;
}

export interface StoreDetailsResponse {
  status?: string;
  message?: string;
  source?: 'session' | 'surfboard-api';
  
  // Direct fields (from session)
  storeId?: string;
  merchantId?: string;
  partnerId?: string;
  name?: string;
  termsAndConditionsURL?: string;
  privacyPolicyURL?: string;
  merchantWebshopURL?: string;
  supportEmail?: string;
  supportPhone?: string;
  branding?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    buttonColor?: string;
    buttonTextColor?: string;
    logoUrl?: string;
    logoWidth?: number;
    fontFamily?: string;
    hideHeader?: boolean;
    hideFooter?: boolean;
    roundedCorners?: boolean;
    borderRadius?: number;
  };
  
  // Nested fields (from Surfboard API)
  data?: {
    storeId: string;
    merchantId: string;
    name: string;
    status: string;
    onlineOnboardingStatus: string;
    address?: {
      careOf?: string | null;
      addressLine1?: string;
      addressLine2?: string;
      addressLine3?: string;
      city?: string;
      countryCode?: string;
      postalCode?: string;
    };
    onlineInfo?: {
      merchantWebshopURL?: string;
      termsAndConditionsURL?: string;
      privacyPolicyURL?: string;
    };
    phone?: string;
    email?: string;
  };
}

export interface PaymentInitiateResponse {
  redirectUrl?: string;
  paymentId: string;
  status: string;
}

// =====================================================
// Fetch wrapper with error handling
// =====================================================

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    // In widget mode (cross-origin), don't send credentials to avoid CORS complications
    const isWidgetMode = !!(window as any).__SAGAPAY_WIDGET_MODE__;
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      credentials: isWidgetMode ? 'omit' : 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    let data: any;
    try {
      data = await response.json();
    } catch {
      // Response was not valid JSON
      if (!response.ok) {
        return { error: { code: 'SERVER_ERROR', message: `Server returned ${response.status}`, retryable: response.status >= 500 } };
      }
      return { error: { code: 'PARSE_ERROR', message: 'Ugyldig svar fra serveren', retryable: true } };
    }
    
    if (!response.ok) {
      // Errors arrive flat — {status:"ERROR", code, message, retryable} — with
      // the legacy nested `error` still present during the transition. Read the
      // flat form first so this keeps working once the nesting is dropped.
      const flat = typeof data.code === 'string'
        ? { code: data.code, message: data.message, retryable: Boolean(data.retryable), method: data.method, details: data.details }
        : null;

      return { error: flat || data.error || { code: 'UNKNOWN', message: 'An error occurred', retryable: false } };
    }
    
    return { data };
  } catch (error) {
    // Keep the real cause visible. A bare "network error" hid a TypeError
    // thrown by page instrumentation for a request that had actually
    // succeeded, and cost a long time to track down.
    console.error(`[SagaPay] Request failed: ${API_BASE}${path}`, error);
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: 'Kunne ikke koble til serveren. Prøv igjen.',
        retryable: true,
      },
    };
  }
}

// =====================================================
// Session API
// =====================================================

export async function getSession(sessionId: string) {
  return apiFetch(`/sessions/${sessionId}`);
}

/** Address as the session endpoint accepts it — every line the form collects. */
interface SessionAddressInput {
  streetAddress: string;
  careOf?: string;
  addressLine2?: string;
  postalCode: string;
  city: string;
  countryCode?: string;
}

export async function updateSession(sessionId: string, data: {
  customer?: {
    email: string;
    /**
     * A string only when we genuinely have nothing better. The object form
     * carries the split the customer typed, so the server does not have to
     * infer it from spaces — see CustomerData.firstName.
     */
    name: string | { fullName: string; firstName?: string; lastName?: string };
    phone?: string;
  };
  shippingAddress?: SessionAddressInput;
  billingAddress?: SessionAddressInput;
}) {
  return apiFetch(`/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Mark session as complete after successful payment
 * This verifies the payment with Surfboard and sends webhook to merchant
 */
export async function completeSession(sessionId: string): Promise<ApiResponse<{
  event: string;
  data: {
    sagaPaySessionId: string;
    surfboardOrderId: string;
    paymentId: string;
    customer: unknown;
    shipping: unknown;
    payment: unknown;
  };
}>> {
  return apiFetch(`/sessions/${sessionId}/complete`, {
    method: 'POST',
  });
}

// =====================================================
// Shipping API
// =====================================================

export async function getShippingOptions(sessionId: string, postalCode: string): Promise<ApiResponse<ShippingOptionsResponse>> {
  return apiFetch<ShippingOptionsResponse>('/shipping/options', {
    method: 'POST',
    body: JSON.stringify({ sessionId, postalCode }),
  });
}

export async function selectShipping(sessionId: string, shippingOptionId: string, price?: number, provider?: string) {
  return apiFetch('/shipping/select', {
    method: 'POST',
    body: JSON.stringify({ sessionId, shippingOptionId, price, provider }),
  });
}

export async function getPickupPoints(postalCode: string, provider?: string) {
  return apiFetch('/shipping/pickup-points', {
    method: 'POST',
    body: JSON.stringify({ postalCode, countryCode: 'NO', provider }),
  });
}

// =====================================================
// Payment API
// =====================================================

export async function getPaymentMethods(sessionId: string, deviceToken?: string | null, force?: boolean): Promise<ApiResponse<PaymentMethodsResponse>> {
  const qs = force ? '?force=1' : '';
  return apiFetch<PaymentMethodsResponse>(`/payment/methods/${sessionId}${qs}`, {
    headers: deviceToken ? { 'X-Device-Token': deviceToken } : {},
  });
}

export async function initiatePayment(sessionId: string, paymentMethod: string): Promise<ApiResponse<PaymentInitiateResponse>> {
  return apiFetch<PaymentInitiateResponse>('/payment/initiate', {
    method: 'POST',
    body: JSON.stringify({ sessionId, paymentMethod }),
  });
}

export async function executeQuickPay(sessionId: string, deviceToken: string): Promise<ApiResponse<PaymentInitiateResponse>> {
  return apiFetch<PaymentInitiateResponse>('/payment/quick-pay', {
    method: 'POST',
    body: JSON.stringify({ sessionId, deviceToken }),
  });
}

export async function verifyPayment(sessionId: string) {
  return apiFetch('/payment/verify', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

export async function savePaymentPreference(sessionId: string, paymentMethod: string, saveForNetwork = false) {
  return apiFetch('/payment/save-preference', {
    method: 'POST',
    body: JSON.stringify({ sessionId, paymentMethod, saveForNetwork }),
  });
}

// =====================================================
// Preferences API
// =====================================================

export async function lookupPreference(email: string, postalCode: string, merchantId: string) {
  return apiFetch('/preferences/lookup', {
    method: 'POST',
    body: JSON.stringify({ email, postalCode, merchantId }),
  });
}

/**
 * The proof this browser saved an address here.
 *
 * Kept in local storage because it must outlive the checkout session — that is
 * the whole point of it: a returning customer, days later, in the same browser.
 * Anyone without it is treated as a first-time customer, which is the correct
 * answer when we cannot tell them apart from a stranger who knows the email.
 */
const CUSTOMER_TOKEN_KEY = 'sagapay.customerToken';

export function readCustomerToken(): string | undefined {
  try {
    return window.localStorage.getItem(CUSTOMER_TOKEN_KEY) || undefined;
  } catch {
    // Private mode, or storage disabled. No token, no prefill — not an error.
    return undefined;
  }
}

function rememberCustomerToken(token: string | undefined) {
  if (!token) return;
  try {
    window.localStorage.setItem(CUSTOMER_TOKEN_KEY, token);
  } catch {
    // Nothing to do: the customer simply gets no prefill next time.
  }
}

/**
 * Saved addresses for a returning customer.
 *
 * `sessionId` is required by the server: an email on its own used to return
 * somebody's home address to anyone who asked for it.
 */
export async function getSavedAddresses(email: string, sessionId: string, postalCode?: string): Promise<ApiResponse<SavedAddressesResponse>> {
  return apiFetch<SavedAddressesResponse>('/preferences/addresses', {
    method: 'POST',
    body: JSON.stringify({ email, postalCode, sessionId, customerToken: readCustomerToken() }),
  });
}

export async function saveAddress(
  email: string,
  address: {
    streetAddress: string;
    postalCode: string;
    city: string;
    countryCode?: string;
  },
  options?: {
    fullName?: string;
    phone?: string;
    isDefault?: boolean;
    consent?: boolean;
  }
) {
  const saved = await apiFetch<{ saved: boolean; addressId: string; customerToken?: string }>('/preferences/addresses/save', {
    method: 'POST',
    body: JSON.stringify({ 
      email, 
      fullName: options?.fullName,
      phone: options?.phone,
      address, 
      isDefault: options?.isDefault ?? false,
      consent: options?.consent ?? true
    }),
  });

  // Keep what the server handed back. It is the only thing that will identify
  // this customer on their next visit — see readCustomerToken().
  rememberCustomerToken(saved.data?.customerToken);
  return saved;
}

export async function lookupPostalCode(postalCode: string): Promise<ApiResponse<PostalCodeResponse>> {
  return apiFetch<PostalCodeResponse>(`/preferences/postnummer/${postalCode}`);
}

/**
 * Delete all saved customer data (GDPR right to erasure)
 * Removes all addresses, preferences, and shipping preferences for an email.
 */
export async function deleteCustomerData(email: string, sessionId: string): Promise<ApiResponse<{ addressesDeleted: number; preferencesDeleted: number }>> {
  const token = readCustomerToken();
  return apiFetch(`/customer/data?email=${encodeURIComponent(email)}&sessionId=${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers: token ? { 'X-Customer-Token': token } : {},
  });
}

// =====================================================
// Store/Merchant API
// =====================================================

/**
 * Get store details from session (preferred method)
 * All config comes from merchant at initialization - no external API calls
 */
export async function getStoreDetailsFromSession(
  sessionId: string
): Promise<ApiResponse<StoreDetailsResponse>> {
  return apiFetch<StoreDetailsResponse>(`/store/session/${sessionId}`);
}

/**
 * Get store details from Surfboard API
 * Terms & privacy URLs are registered in the SagaPay system
 */
export async function getStoreDetails(
  merchantId: string,
  storeId?: string,
  sessionId?: string
): Promise<ApiResponse<StoreDetailsResponse>> {
  // The session tells the server whether to read the sandbox or the live
  // store; without it the server can only guess from its own default.
  const params = new URLSearchParams();
  if (storeId) params.set('storeId', storeId);
  if (sessionId) params.set('session', sessionId);
  const query = params.toString() ? `?${params}` : '';
  return apiFetch<StoreDetailsResponse>(`/store/${merchantId}/details${query}`);
}

// Legacy endpoint with explicit partnerId/storeId
export async function getStoreDetailsLegacy(
  partnerId: string, 
  merchantId: string, 
  storeId: string
): Promise<ApiResponse<StoreDetailsResponse>> {
  return apiFetch<StoreDetailsResponse>(`/store/${partnerId}/${merchantId}/${storeId}`);
}

export interface MerchantPaymentMethodsResponse {
  status: string;
  data: Array<{
    paymentMethodId?: string;
    paymentMethod: string;
  }>;
  cached?: boolean;
  fallback?: boolean;
}

/**
 * Activated payment methods.
 *
 * Pass the sessionId whenever you have one: it lets the API answer from the
 * session's terminal — what this checkout can actually offer — instead of the
 * merchant-wide list, which includes in-person methods and omits the online
 * wallets.
 */
export async function getMerchantPaymentMethods(
  merchantId: string,
  sessionId?: string
): Promise<ApiResponse<MerchantPaymentMethodsResponse>> {
  const qs = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
  return apiFetch<MerchantPaymentMethodsResponse>(`/store/${merchantId}/payment-methods${qs}`);
}

// =====================================================
// Polling utility for payment verification
// =====================================================

export function pollPaymentStatus(
  sessionId: string,
  onSuccess: (data: any) => void,
  onError: (error: any) => void,
  maxAttempts = 30,
  intervalMs = 2000
) {
  let attempts = 0;
  
  const poll = async () => {
    attempts++;
    
    const result = await verifyPayment(sessionId);
    
    if (result.error) {
      if (result.error.retryable && attempts < maxAttempts) {
        setTimeout(poll, intervalMs);
      } else {
        onError(result.error);
      }
      return;
    }
    
    const data = result.data as any;
    
    if (data.isComplete) {
      onSuccess(data);
    } else if (data.paymentStatus === 'PAYMENT_CANCELLED') {
      onError({ code: 'PAYMENT_CANCELLED', message: 'Betalingen ble avbrutt' });
    } else if (attempts < maxAttempts) {
      // Exponential backoff
      const nextInterval = Math.min(intervalMs * Math.pow(1.2, attempts - 1), 10000);
      setTimeout(poll, nextInterval);
    } else {
      onError({ code: 'TIMEOUT', message: 'Tidsavbrudd. Prøv igjen.' });
    }
  };
  
  poll();
  
  // Return cancel function
  return () => {
    attempts = maxAttempts;
  };
}



/**
 * Start a redirect payment (Vipps, Klarna, Swish…) on the server.
 *
 * The browser SDK normally does this, but it cannot initialise at all when the
 * terminal has no PCI secure channel — which takes down wallets that never
 * touch a card number along with card itself. This path goes through our API
 * instead, so those methods keep working.
 *
 * The response says where to send the customer:
 *   { kind: 'form', url, fields } — POST a self-submitting form
 *   { kind: 'url', url }          — plain redirect
 *   { kind: 'none' }              — started, nothing to redirect to
 */
export interface PaymentStartResult {
  kind: 'form' | 'url' | 'none';
  url?: string;
  fields?: Record<string, string>;
  paymentId?: string;
  paymentMethod?: string;
}

export async function startRedirectPayment(
  sessionId: string,
  paymentMethod: string
): Promise<ApiResponse<PaymentStartResult>> {
  return apiFetch<PaymentStartResult>('/payment/start', {
    method: 'POST',
    body: JSON.stringify({ sessionId, paymentMethod }),
  });
}
